import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Loader2, Pencil, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { validateCampaignBudget } from '@/utils/campaignCreate';
import {
  applyCampaignBudgetUpdate,
  campaignBudgetApiErrorMessage,
  formatBudgetUpdateSuccessMessage,
  type EditableCampaignBudgetTarget,
} from '@/api/campaignBudgetUpdate';
import { cn } from '@/utils/cn';

type EditableCampaignBudgetCellProps = {
  profileId: string;
  displayValue: string;
  editableTarget: EditableCampaignBudgetTarget | null;
  campaignLabel?: string;
  onUpdated?: () => void;
  className?: string;
  /** Table cells use a bordered chip; summary cards use inline text + pencil. */
  variant?: 'table' | 'inline';
};

function parseDraftBudget(draft: string): number | null {
  const trimmed = draft.trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

export function EditableCampaignBudgetCell({
  profileId,
  displayValue,
  editableTarget,
  campaignLabel,
  onUpdated,
  className,
  variant = 'table',
}: EditableCampaignBudgetCellProps) {
  const { showToast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [draftBudget, setDraftBudget] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const mutation = useMutation({
    mutationFn: async (newBudget: number) => {
      if (!editableTarget) throw new Error('Budget is not editable');
      await applyCampaignBudgetUpdate(profileId, editableTarget, newBudget);
      return newBudget;
    },
    onSuccess: (newBudget) => {
      showToast(formatBudgetUpdateSuccessMessage(newBudget), 'success');
      setIsOpen(false);
      setFieldError(null);
      onUpdated?.();
    },
    onError: (error) => {
      const message = campaignBudgetApiErrorMessage(error);
      setFieldError(message);
      showToast(
        `Failed to update budget${campaignLabel ? ` for ${campaignLabel}` : ''}: ${message}`,
        'error'
      );
    },
  });

  useEffect(() => {
    if (!isOpen || variant !== 'table') return;
    const handlePointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
        setFieldError(null);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        setFieldError(null);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, variant]);

  const openEditor = () => {
    if (!editableTarget) return;
    setDraftBudget(String(editableTarget.budget));
    setFieldError(null);
    setIsOpen(true);
  };

  const cancelEditor = () => {
    setIsOpen(false);
    setFieldError(null);
  };

  const handleApply = () => {
    if (!editableTarget) return;
    const parsed = parseDraftBudget(draftBudget);
    if (parsed == null) {
      const message = 'Enter a valid budget amount';
      setFieldError(message);
      if (variant === 'table') showToast(message, 'error');
      return;
    }
    const validationError = validateCampaignBudget(
      parsed,
      editableTarget.adProduct,
      editableTarget.budgetType
    );
    if (validationError) {
      setFieldError(validationError);
      if (variant === 'table') showToast(validationError, 'error');
      return;
    }
    if (parsed === editableTarget.budget) {
      cancelEditor();
      return;
    }
    setFieldError(null);
    mutation.mutate(parsed);
  };

  if (!editableTarget) {
    return <span className={className}>{displayValue}</span>;
  }

  const budgetTypeLabel = editableTarget.budgetType || 'DAILY';

  if (variant === 'inline') {
    if (!isOpen) {
      return (
        <div
          className={cn(
            'group inline-flex max-w-full items-center gap-0.5 rounded-md -mx-1 px-1 py-0.5 transition-colors hover:bg-brand-subtle/50',
            className
          )}
        >
          <span>{displayValue}</span>
          <button
            type="button"
            onClick={openEditor}
            className="inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-brand transition-colors hover:bg-brand-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
            aria-label={campaignLabel ? `Edit budget for ${campaignLabel}` : 'Edit budget'}
            title="Edit budget"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      );
    }

    return (
      <div ref={containerRef} className={cn('space-y-1', className)}>
        <div className="flex flex-wrap items-center gap-1">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draftBudget}
            onChange={(event) => {
              setDraftBudget(event.target.value);
              if (fieldError) setFieldError(null);
            }}
            className="h-7 w-[5.5rem] px-2 py-1 text-sm tabular-nums"
            autoFocus
            disabled={mutation.isPending}
            aria-label="Campaign budget"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleApply();
              }
              if (event.key === 'Escape') {
                event.preventDefault();
                cancelEditor();
              }
            }}
          />
          <span className="text-foreground-muted text-sm whitespace-nowrap">({budgetTypeLabel})</span>
          <button
            type="button"
            onClick={handleApply}
            disabled={mutation.isPending}
            className="inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-brand hover:bg-brand-subtle disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
            aria-label="Save budget"
            title="Save"
          >
            {mutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <Check className="h-3.5 w-3.5" aria-hidden />
            )}
          </button>
          <button
            type="button"
            onClick={cancelEditor}
            disabled={mutation.isPending}
            className="inline-flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-muted hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
            aria-label="Cancel editing budget"
            title="Cancel"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
        {fieldError ? <p className="text-xs text-error">{fieldError}</p> : null}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={cn('relative inline-block text-right', className)}>
      <button
        type="button"
        onClick={openEditor}
        className="inline-flex h-7 min-w-[4.75rem] cursor-pointer items-center justify-center rounded-lg border border-border bg-surface px-2 font-mono text-body-sm tabular-nums text-foreground transition-colors hover:border-brand hover:bg-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/30"
        aria-label={campaignLabel ? `Edit budget for ${campaignLabel}` : 'Edit budget'}
      >
        {displayValue}
      </button>

      {isOpen ? (
        <div className="absolute right-0 top-full z-20 mt-1 w-44 rounded-xl border border-border bg-surface p-3 shadow-lg">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draftBudget}
            onChange={(event) => {
              setDraftBudget(event.target.value);
              if (fieldError) setFieldError(null);
            }}
            className="mb-2 h-7 px-2 py-1 text-right font-mono text-sm"
            autoFocus
            disabled={mutation.isPending}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                handleApply();
              }
            }}
          />
          {fieldError ? <p className="mb-2 text-xs text-error">{fieldError}</p> : null}
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={cancelEditor}
              disabled={mutation.isPending}
            >
              Cancel
            </Button>
            <Button type="button" size="sm" onClick={handleApply} disabled={mutation.isPending}>
              {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Apply'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
